import React, { useEffect, useState } from "react"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"
import { Loader } from "shared/ui/Loader/Loader.component"
import { KindeRolesClaim, KindeOrgPropertiesClaim } from "types/kinde.types"
import { USER_ROLE } from "types/userRole.types"
import { getServerFromHostname, serverOrigin, ServerId } from "utils/server"
import { setServerCookie } from "utils/serverCookies"

const REDIRECTED_FLAG = "dq_server_redirected"

/**
 * Enforces that an authenticated user is on their organization's server
 * subdomain. Rendered inside ProtectedRoutes, so tokens are guaranteed.
 *
 * - Non-server hosts (localhost, staging, the bare domain): no enforcement.
 * - Superadmins: never redirected; they switch servers via the unit switcher.
 * - server_id claim missing or matching: pass through (the conductor's own
 *   server check is the backstop).
 * - Mismatch: set the dq_server cookie and redirect to the user's server
 *   subdomain, where Kinde silently re-establishes the session.
 *
 * Loop protection: the redirect target is the token's own server, so the
 * guard passes on arrival. The sessionStorage flag additionally breaks any
 * A->B->A ping-pong after one round trip.
 */
const ServerGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getClaim } = useKindeAuth()
  const [status, setStatus] = useState<"checking" | "ok" | "redirecting">(
    "checking"
  )

  useEffect(() => {
    const enforceServer = async (): Promise<void> => {
      const hostServer = getServerFromHostname()
      if (!hostServer) {
        setStatus("ok")
        return
      }

      const roles = (await getClaim("roles")) as KindeRolesClaim | null
      const isSuperAdmin =
        roles?.value?.some((role) => role.key === USER_ROLE.SUPER_ADMIN) ??
        false
      if (isSuperAdmin) {
        setServerCookie(hostServer)
        setStatus("ok")
        return
      }

      const orgProperties = (await getClaim(
        "organization_properties"
      )) as KindeOrgPropertiesClaim | null
      const tokenServer = orgProperties?.value?.server_id?.v?.toLowerCase() as
        | ServerId
        | undefined

      if (!tokenServer || tokenServer === hostServer) {
        setServerCookie(hostServer)
        sessionStorage.removeItem(REDIRECTED_FLAG)
        sessionStorage.removeItem("dq_403_handled")
        setStatus("ok")
        return
      }

      if (sessionStorage.getItem(REDIRECTED_FLAG)) {
        // Already bounced once this tab; the target server sent us back or
        // is unavailable. Stay put rather than loop.
        setStatus("ok")
        return
      }
      sessionStorage.setItem(REDIRECTED_FLAG, "1")
      setServerCookie(tokenServer)
      setStatus("redirecting")
      window.location.replace(
        serverOrigin(tokenServer) +
          window.location.pathname +
          window.location.search
      )
    }

    enforceServer().catch(() => setStatus("ok"))
  }, [getClaim])

  if (status !== "ok") {
    return (
      <div className="absolute left-2/4 top-2/4">
        <Loader fontSize={48} color="#36C3ED" />
      </div>
    )
  }

  return <>{children}</>
}

export default ServerGuard
